import { supabase } from '../../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams, getDomainFilterParamsAsync } from '../../hooks/useDomainQuery';
import { Product, Category, ProductImage, Order } from '../shopService';
import { generateSlug } from '../shopService';

// --- Fonctions d'Administration pour les Produits ---

/**
 * Récupère un produit par son ID pour l'administration
 * @param productId - ID du produit à récupérer
 * @param domain - Domaine pour filtrer le produit (optionnel)
 */
export async function adminGetProduct(productId: number, domain?: string) {
  try {
    // Construire la requête de base
    let query = supabase
      .from('shop_products')
      .select(`
        *,
        category:category_id(id, name, slug),
        images:shop_product_images(*)
      `)
      .eq('id', productId);

    // Appliquer le filtre domaine si fourni
    if (domain) {
      query = applyDomainFilterToQuery(query);
    }

    const { data, error } = await query.single();

    // S'assurer que materials est un tableau
    if (data) {
      data.materials = data.materials || [];
    }

    if (error) throw error;
    return data as Product;
  } catch (error) {
    console.error('Erreur lors de la récupération du produit (admin):', error);
    throw error;
  }
}

/**
 * Récupère tous les produits pour l'administration
 * @param domain - Domaine pour filtrer les produits (optionnel)
 */
export async function adminGetAllProducts(domain?: string) {
  try {
    // Construire la requête de base
    let query = supabase
      .from('shop_products')
      .select(`
        *,
        category:category_id(id, name, slug),
        images:shop_product_images(*)
      `)
      .order('created_at', { ascending: false });

    // Appliquer le filtre domaine si fourni
    if (domain) {
      query = applyDomainFilterToQuery(query);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Log pour déboguer la structure des données
    console.log('Données brutes des produits:', JSON.stringify(data, null, 2));

    // S'assurer que chaque produit a un tableau d'images
    const productsWithImages = data.map(product => ({
      ...product,
      images: product.images || []
    }));

    return productsWithImages as Product[];
  } catch (error) {
    console.error('Erreur lors de la récupération des produits (admin):', error);
    throw error;
  }
}

/**
 * Crée un nouveau produit
 */
export async function adminCreateProduct(productData: Omit<Product, 'id' | 'created_at'> & { 
  images?: File[]; 
  domain?: string; 
  tenant_id?: number; 
}) {
  try {
    // Générer un slug s'il n'est pas fourni
    if (!productData.slug) {
      productData.slug = generateSlug(productData.title);
    }

    // Vérifier si le slug existe déjà
    const { data: existingProduct } = await supabase
      .from('shop_products')
      .select('id')
      .eq('slug', productData.slug)
      .maybeSingle();

    if (existingProduct) {
      // Ajouter un suffixe aléatoire au slug
      productData.slug = `${productData.slug}-${Math.floor(Math.random() * 1000)}`;
    }

    // Extraire les images du productData avant insertion
    const images = productData.images;
    delete (productData as any).images;

    // S'assurer que materials est un tableau
    const productToInsert = {
      ...productData,
      materials: Array.isArray(productData.materials) ? productData.materials : []
    };

    console.log('Insertion du produit avec matériaux:', productToInsert);

    // Insérer le produit
    const { data: product, error } = await supabase
      .from('shop_products')
      .insert(productToInsert)
      .select()
      .single();

    if (error) throw error;

    // Uploader et associer les images si présentes
    if (images && images.length > 0) {
      await uploadProductImages(product.id, images, productData.domain, productData.tenant_id?.toString());
    }

    return product;
  } catch (error) {
    console.error('Erreur lors de la création du produit:', error);
    throw error;
  }
}

/**
 * Met à jour un produit existant
 */
export async function adminUpdateProduct(
  productId: number, 
  productData: Partial<Product> & { 
    newImages?: File[]; 
    deletedImageIds?: number[]; 
    domain?: string; 
    tenant_id?: number; 
  }
) {
  try {
    // Extraire les nouvelles images et les IDs d'images à supprimer
    const newImages = productData.newImages;
    const deletedImageIds = productData.deletedImageIds;
    
    delete (productData as any).newImages;
    delete (productData as any).deletedImageIds;

    // S'assurer que materials est un tableau
    const productToUpdate = {
      ...productData,
      materials: Array.isArray(productData.materials) ? productData.materials : []
    };

    console.log('Mise à jour du produit avec matériaux:', productToUpdate);

    // Mettre à jour le produit
    const { data: product, error } = await supabase
      .from('shop_products')
      .update(productToUpdate)
      .eq('id', productId)
      .select()
      .single();

    if (error) throw error;

    // Supprimer les images si nécessaire
    if (deletedImageIds && deletedImageIds.length > 0) {
      await deleteProductImages(deletedImageIds);
    }

    // Uploader les nouvelles images si présentes
    if (newImages && newImages.length > 0) {
      await uploadProductImages(productId, newImages, productData.domain, productData.tenant_id?.toString());
    }

    return product;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du produit ${productId}:`, error);
    throw error;
  }
}

/**
 * Supprime un produit
 */
export async function adminDeleteProduct(productId: number) {
  try {
    // Vérifier d'abord s'il y a des commandes associées
    const { data: orderItems, error: orderItemsError } = await supabase
      .from('shop_order_items')
      .select('id')
      .eq('product_id', productId);

    if (orderItemsError) throw orderItemsError;

    if (orderItems && orderItems.length > 0) {
      throw new Error(`Impossible de supprimer ce produit car il est référencé dans ${orderItems.length} commande(s) existante(s). Vous pouvez le désactiver à la place.`);
    }

    // Supprimer d'abord les images associées
    const { data: images, error: imagesError } = await supabase
      .from('shop_product_images')
      .select('id')
      .eq('product_id', productId);

    if (imagesError) throw imagesError;

    if (images && images.length > 0) {
      await deleteProductImages(images.map(img => img.id));
    }

    // Puis supprimer le produit
    const { error } = await supabase
      .from('shop_products')
      .delete()
      .eq('id', productId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Erreur lors de la suppression du produit ${productId}:`, error);
    throw error;
  }
}

/**
 * Désactive/Active un produit (alternative à la suppression)
 */
export async function adminToggleProductStatus(productId: number, isActive: boolean) {
  try {
    const status = isActive ? 'available' : 'unavailable';
    const { error } = await supabase
      .from('shop_products')
      .update({ 
        status: status
      })
      .eq('id', productId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Erreur lors de la modification du statut du produit ${productId}:`, error);
    throw error;
  }
}

/**
 * Duplique un produit existant (sans son ID)
 * @param productId ID du produit à dupliquer
 * @param domain Domaine pour filtrer le produit (optionnel)
 * @returns Le nouveau produit créé
 */
export async function adminDuplicateProduct(productId: number, domain?: string) {
  try {
    // Récupérer le produit existant
    const { data: product, error: fetchError } = await supabase
      .from('shop_products')
      .select('*')
      .eq('id', productId)
      .single();

    if (fetchError || !product) {
      throw new Error('Produit non trouvé');
    }

    // Récupérer les images du produit
    const { data: images, error: imagesError } = await supabase
      .from('shop_product_images')
      .select('*')
      .eq('product_id', productId);

    if (imagesError) throw imagesError;

    // Créer une copie du produit sans l'ID et les champs de date
    const { id, created_at, updated_at, slug, ...productData } = product;
    
    // Mettre à jour le titre pour indiquer qu'il s'agit d'une copie
    const newTitle = `[Copie] ${productData.title}`;
    const newSlug = `${productData.slug}-${Date.now()}`;

    // Préparer les données du nouveau produit
    const newProductData = { ...productData };
    
    // Supprimer les champs de date s'ils existent
    delete newProductData.created_at;
    delete newProductData.updated_at;

    // Créer le nouveau produit
    const { data: newProduct, error: createError } = await supabase
      .from('shop_products')
      .insert([
        { 
          ...newProductData,
          title: newTitle,
          slug: newSlug
        }
      ])
      .select()
      .single();

    if (createError || !newProduct) {
      throw createError || new Error('Erreur lors de la création de la copie du produit');
    }

    // Si le produit original avait des images, les dupliquer
    if (images && images.length > 0) {
      await supabase
        .from('shop_product_images')
        .insert(
          images.map(img => ({
            product_id: newProduct.id,
            image_url: img.image_url,
            is_featured: img.is_featured,
            alt_text: img.alt_text,
            created_at: new Date().toISOString()
          }))
        );
    }

    // Récupérer le produit complet avec les relations
    return await adminGetProduct(newProduct.id, domain);
  } catch (error) {
    console.error('Erreur lors de la duplication du produit:', error);
    throw error;
  }
}

// --- Fonctions d'Administration pour les Catégories ---

/**
 * Récupère toutes les catégories
 */
export async function adminGetAllCategories() {
  try {
    // Récupérer d'abord les produits du domaine pour filtrer les catégories
    let productsQuery = supabase
      .from('shop_products')
      .select('category_id');
    
    // Appliquer le filtre domaine
    productsQuery = applyDomainFilterToQuery(productsQuery);
    
    const { data: products, error: productsError } = await productsQuery;
    
    if (productsError) {
      console.error('Erreur lors de la récupération des produits:', productsError);
      throw productsError;
    }
    
    // Si aucun produit n'existe pour ce tenant, retourner toutes les catégories
    // car l'administrateur pourrait vouloir ajouter de nouveaux produits
    if (!products || products.length === 0) {
      const { data, error } = await supabase
        .from('shop_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as Category[];
    }
    
    // Récupérer les IDs des catégories uniques
    const categoryIds = [...new Set(products.map(p => p.category_id).filter(Boolean))];
    
    // Récupérer toutes les catégories, mais marquer celles qui sont utilisées
    const { data, error } = await supabase
      .from('shop_categories')
      .select('*')
      .order('name');

    if (error) throw error;
    
    // Ajouter un indicateur pour les catégories utilisées par ce tenant
    const categoriesWithUsage = data.map(category => ({
      ...category,
      is_used_by_tenant: categoryIds.includes(category.id)
    }));

    return categoriesWithUsage as (Category & { is_used_by_tenant: boolean })[];
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories (admin):', error);
    throw error;
  }
}

/**
 * Crée une nouvelle catégorie
 */
export async function adminCreateCategory(categoryData: Omit<Category, 'id' | 'created_at'>, domain?: string, tenantId?: string) {
  try {
    // Utiliser les paramètres fournis ou récupérer depuis getDomainFilterParams
    const { domain: currentDomain, tenantId: currentTenantId } = getDomainFilterParams();
    const finalDomain = domain || currentDomain;
    const finalTenantId = tenantId || currentTenantId;
    
    // Générer un slug s'il n'est pas fourni
    if (!categoryData.slug) {
      categoryData.slug = generateSlug(categoryData.name);
    }

    // Vérifier si le slug existe déjà
    const { data: existingCategory } = await supabase
      .from('shop_categories')
      .select('id')
      .eq('slug', categoryData.slug)
      .maybeSingle();

    if (existingCategory) {
      // Ajouter un suffixe aléatoire au slug
      categoryData.slug = `${categoryData.slug}-${Math.floor(Math.random() * 1000)}`;
    }

    const { data, error } = await supabase
      .from('shop_categories')
      .insert({
        ...categoryData,
        tenant_id: parseInt(finalTenantId),
        domain: finalDomain
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Erreur lors de la création de la catégorie:', error);
    throw error;
  }
}

/**
 * Met à jour une catégorie existante
 */
export async function adminUpdateCategory(categoryId: number, categoryData: Partial<Category>, domain?: string, tenantId?: string) {
  try {
    // Utiliser les paramètres fournis ou récupérer depuis getDomainFilterParams
    const { domain: currentDomain, tenantId: currentTenantId } = getDomainFilterParams();
    const finalDomain = domain || currentDomain;
    const finalTenantId = tenantId || currentTenantId;
    
    let query = supabase
      .from('shop_categories')
      .update({
        ...categoryData,
        domain: finalDomain
      })
      .eq('id', categoryId);
    
    // Appliquer le filtre domaine
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.select().single();

    if (error) throw error;

    return data as Category;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de la catégorie ${categoryId}:`, error);
    throw error;
  }
}

/**
 * Supprime une catégorie
 */
export async function adminDeleteCategory(categoryId: number) {
  try {
    // Vérifier si la catégorie est utilisée par des produits de ce domaine
    let countQuery = supabase
      .from('shop_products')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId);
    
    // Appliquer le filtre domaine
    countQuery = applyDomainFilterToQuery(countQuery);
    
    const { count, error: countError } = await countQuery;

    if (countError) throw countError;

    if (count && count > 0) {
      throw new Error(`Impossible de supprimer la catégorie : ${count} produit(s) y sont associés.`);
    }

    // Vérifier si la catégorie est utilisée par d'autres tenants
    const { count: otherTenantsCount, error: otherTenantsError } = await supabase
      .from('shop_products')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', categoryId)
      .neq('tenant_id', import.meta.env.VITE_TENANT_ID || '2');
      
    if (otherTenantsError) throw otherTenantsError;
    
    // Si la catégorie est utilisée par d'autres tenants, ne pas la supprimer
    if (otherTenantsCount && otherTenantsCount > 0) {
      return { 
        success: true, 
        message: "Catégorie détachée de ce tenant mais conservée car utilisée par d'autres tenants"
      };
    }

    // Sinon, supprimer la catégorie
    let deleteQuery = supabase
      .from('shop_categories')
      .delete()
      .eq('id', categoryId);
    
    // Appliquer le filtre domaine
    deleteQuery = applyDomainFilterToQuery(deleteQuery);
    
    const { error } = await deleteQuery;

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error(`Erreur lors de la suppression de la catégorie ${categoryId}:`, error);
    throw error;
  }
}

// --- Fonctions d'Administration pour les Commandes ---

/**
 * Récupère toutes les commandes
 */
export async function adminGetAllOrders() {
  try {
    // Récupérer les paramètres de domaine
    const { domain, tenantId } = getDomainFilterParams();
    console.log('🔍 adminGetAllOrders - Paramètres:', { domain, tenantId });
    
    // La fonction RPC get_all_orders_with_details a un problème : elle cherche une colonne 'domain' 
    // dans la table 'tenants' qui n'existe pas (c'est 'domain_name')
    // Utilisons directement la requête avec filtrage par domaine
    let ordersQuery = supabase
      .from('shop_orders')
      .select('*')
      .order('created_at', { ascending: false });
    
    // Appliquer le filtrage par domaine (priorité absolue)
    if (domain) {
      ordersQuery = ordersQuery.eq('domain', domain);
    } else {
      ordersQuery = ordersQuery.eq('tenant_id', tenantId);
    }
    
    const { data: ordersData, error: ordersError } = await ordersQuery;
    console.log('📦 adminGetAllOrders - Query result:', { data: ordersData?.length || 0, error: ordersError });
    
    if (ordersError) {
      console.error('Erreur avec la requête directe:', ordersError);
      return [];
    }
    
    // Ajouter les champs manquants pour maintenir la structure attendue
    return ordersData.map((order: any) => ({
      id: order.id,
      order_number: order.order_number,
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      status: order.status,
      payment_status: order.payment_status || order.status,
      total_amount: order.total_amount,
      currency: order.currency || 'XAF',
      created_at: order.created_at,
      tenant_id: order.tenant_id,
      domain: order.domain
    }));
  } catch (error) {
    console.error('Erreur lors de la récupération des commandes (admin):', error);
    // En cas d'erreur, renvoyer un tableau vide pour éviter les erreurs de permission
    return [];
  }
}

/**
 * Récupère une commande par son ID
 */
export async function adminGetOrderById(orderId: number) {
  try {
    let query = supabase
      .from('shop_orders')
      .select(`
        *,
        items:shop_order_items(
          *,
          product:product_id(
            id,
            title,
            price,
            images:shop_product_images(*)
          )
        )
      `)
      .eq('id', orderId);
    
    // Appliquer le filtre domaine
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de la commande ${orderId}:`, error);
    throw error;
  }
}

/**
 * Met à jour le statut d'une commande
 */
export async function adminUpdateOrderStatus(orderId: number, status: Order['status'], notes?: string) {
  try {
    const { tenantId } = getDomainFilterParams();
    
    const updateData: { status: Order['status']; notes?: string } = { status };
    
    if (notes !== undefined) {
      updateData.notes = notes;
    }
    
    // Vérifier d'abord que la commande appartient bien au tenant
    const { data: orderData, error: checkError } = await supabase
      .from('shop_orders')
      .select('id')
      .eq('id', orderId);
    
    // Appliquer le filtre domaine
    const { data: orderDataDomain, error: checkErrorDomain } = await supabase
      .from('shop_orders')
      .select('id')
      .eq('id', orderId)
      .eq('tenant_id', tenantId)
      .single();
    
    if (checkErrorDomain) {
      throw new Error(`La commande ${orderId} n'existe pas ou n'appartient pas à ce tenant`);
    }
    
    let query = supabase
      .from('shop_orders')
      .update(updateData)
      .eq('id', orderId);
    
    // Appliquer le filtre domaine
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.select().single();

    if (error) throw error;

    // Si le statut est passé à "paid", le trigger SQL mettra à jour le stock automatiquement

    // Envoyer une notification par email en fonction du nouveau statut
    await fetch('/api/shop/order-status-update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        newStatus: status,
        tenantId
      }),
    });

    return data;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du statut de la commande ${orderId}:`, error);
    throw error;
  }
}

// --- Fonctions utilitaires ---

/**
 * Upload des images pour un produit
 */
async function uploadProductImages(productId: number, images: File[], domain?: string, tenantId?: string) {
  try {
    // Utiliser les paramètres fournis ou récupérer depuis la base de données
    let finalDomain = domain;
    let finalTenantId = tenantId;
    
    if (!finalDomain || !finalTenantId) {
      const resolvedParams = await getDomainFilterParamsAsync();
      finalDomain = finalDomain || resolvedParams.domain || undefined;
      finalTenantId = finalTenantId || resolvedParams.tenantId;
    }
    
    // Vérifications de sécurité
    if (!finalTenantId) {
      throw new Error('Impossible de déterminer le tenant_id pour l\'upload des images');
    }
    
    for (let i = 0; i < images.length; i++) {
      const file = images[i];
      
      // Vérifier si l'image provient de la médiathèque (a une selectedUrl)
      if ((file as any).selectedUrl) {
        // Image sélectionnée depuis la médiathèque, utiliser directement l'URL
        const { error: insertError } = await supabase
          .from('shop_product_images')
          .insert({
            product_id: productId,
            image_url: (file as any).selectedUrl,
            alt_text: file.name.split('.')[0] || 'Image produit',
            is_featured: (file as any).is_featured || (i === 0), // La première image est mise en avant par défaut
            domain: finalDomain || null,
            tenant_id: parseInt(finalTenantId)
          });

        if (insertError) throw insertError;
      } else {
        // Nouvelle image à uploader
        const fileExt = file.name.split('.').pop();
        const fileName = `product-${Date.now()}-${Math.floor(Math.random() * 1000)}.${fileExt}`;
        const filePath = `tenant/${finalTenantId}/${fileName}`;

        // Upload de l'image au storage
        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Récupérer l'URL publique
        const { data: { publicUrl } } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        // Associer l'image au produit
        const { error: insertError } = await supabase
          .from('shop_product_images')
          .insert({
            product_id: productId,
            image_url: publicUrl,
            alt_text: file.name.split('.')[0],
            is_featured: (file as any).is_featured || (i === 0), // La première image est mise en avant par défaut
            domain: finalDomain || null,
            tenant_id: parseInt(finalTenantId)
          });

        if (insertError) throw insertError;
      }
    }
  } catch (error) {
    console.error(`Erreur lors de l'upload des images pour le produit ${productId}:`, error);
    throw error;
  }
}

/**
 * Supprime des images de produit
 */
async function deleteProductImages(imageIds: number[]) {
  try {
    // Récupérer les URLs des images
    const { data: images, error: fetchError } = await supabase
      .from('shop_product_images')
      .select('image_url')
      .in('id', imageIds);

    if (fetchError) throw fetchError;

    // Supprimer les entrées de la base de données
    const { error: deleteError } = await supabase
      .from('shop_product_images')
      .delete()
      .in('id', imageIds);

    if (deleteError) throw deleteError;

    // Supprimer les fichiers du storage
    if (images && images.length > 0) {
      for (const image of images) {
        // Extraction du chemin à partir de l'URL complète
        const storageUrlPattern = /\/storage\/v1\/object\/public\//;
        const path = image.image_url.replace(storageUrlPattern, '');
        await supabase.storage.from(path.split('/')[0]).remove([path.split('/').slice(1).join('/')]);
      }
    }
  } catch (error) {
    console.error(`Erreur lors de la suppression des images:`, error);
    throw error;
  }
}
